import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import Modal from '../../components/common/Modal';

const ZONE_NAMES = ['EST', 'EST 2', 'CENTRE EST', 'CENTRE', 'CENTRE-B', 'SUD', 'CENTRE OUEST', 'OUEST'];

const ZoneManagement = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);

  const [selectedZone, setSelectedZone] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [chefsLoading, setChefsLoading] = useState(false);
  const [selectedChefId, setSelectedChefId] = useState('');
  const [newZoneName, setNewZoneName] = useState(ZONE_NAMES[0]);

  const [assignLoading, setAssignLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/zones');
      setZones(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des zones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const openAssignModal = async (zone) => {
    setSelectedZone(zone);
    setSelectedChefId(zone.chefDeZoneId?._id || '');
    setChefs([]);
    setChefsLoading(true);
    setShowAssignModal(true);
    try {
      const response = await api.users.getAll();
      const allUsers = response.data.data || [];
      setChefs(allUsers.filter(u => u.role === 'chef_de_zone'));
    } catch (err) {
      toast.error('Erreur lors du chargement des chefs de zone');
    } finally {
      setChefsLoading(false);
    }
  };

  const openToggleModal = (zone) => {
    setSelectedZone(zone);
    setShowToggleModal(true);
  };

  const handleAssignChef = async () => {
    if (!selectedZone || !selectedChefId) return;
    try {
      setAssignLoading(true);
      await api.put(`/api/zones/${selectedZone._id}/assign-chef`, { chefDeZoneId: selectedChefId });
      toast.success('Chef de zone assigné avec succès');
      setShowAssignModal(false);
      fetchZones();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'assignation du chef de zone');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleToggleZone = async () => {
    if (!selectedZone) return;
    try {
      setToggleLoading(true);
      await api.put(`/api/zones/${selectedZone._id}/toggle`);
      toast.success(selectedZone.isActive ? 'Zone désactivée avec succès' : 'Zone activée avec succès');
      setShowToggleModal(false);
      fetchZones();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la modification du statut');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleCreateZone = async () => {
    if (!newZoneName) return;
    try {
      setCreateLoading(true);
      await api.post('/api/zones', { name: newZoneName });
      toast.success(`Zone ${newZoneName} créée avec succès`);
      setShowCreateModal(false);
      setNewZoneName(ZONE_NAMES[0]);
      fetchZones();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la création de la zone');
    } finally {
      setCreateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Zones</h1>
        <button
          onClick={() => {
            setNewZoneName(ZONE_NAMES[0]);
            setShowCreateModal(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Créer une zone
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chef de Zone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animateurs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distributeurs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {zones.length > 0 ? (
                zones.map((zone) => (
                  <tr key={zone._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{zone.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {zone.chefDeZoneId ? zone.chefDeZoneId.name : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {zone.animateurs ? zone.animateurs.length : 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {zone.distributeurs ? zone.distributeurs.length : 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {zone.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openAssignModal(zone)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Assigner Chef
                      </button>
                      <button
                        onClick={() => openToggleModal(zone)}
                        className={zone.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                      >
                        {zone.isActive ? 'Désactiver' : 'Activer'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucune zone trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Chef Modal */}
      {showAssignModal && selectedZone && (
        <Modal
          open
          onClose={() => setShowAssignModal(false)}
          title={`Assigner un Chef de Zone — ${selectedZone.name}`}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowAssignModal(false)}
                disabled={assignLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAssignChef}
                disabled={assignLoading || chefsLoading || !selectedChefId}
                className="modal-btn-primary"
              >
                {assignLoading ? 'Assignation...' : 'Confirmer'}
              </button>
            </>
          }
        >
          <div>
                {chefsLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="chefSelect" className="block text-sm font-medium text-gray-700 mb-1">
                      Chef de Zone
                    </label>
                    {chefs.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucun chef de zone disponible.</p>
                    ) : (
                      <select
                        id="chefSelect"
                        value={selectedChefId}
                        onChange={(e) => setSelectedChefId(e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="">-- Sélectionner un chef de zone --</option>
                        {chefs.map((chef) => (
                          <option key={chef._id} value={chef._id}>
                            {chef.name} ({chef.email})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
          </div>
        </Modal>
      )}

      {/* Toggle Zone Modal */}
      {showToggleModal && selectedZone && (
        <Modal
          open
          onClose={() => setShowToggleModal(false)}
          title={selectedZone.isActive ? 'Désactiver la zone' : 'Activer la zone'}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowToggleModal(false)}
                disabled={toggleLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleToggleZone}
                disabled={toggleLoading}
                className={selectedZone.isActive ? 'modal-btn-danger' : 'modal-btn-success'}
              >
                {toggleLoading
                  ? (selectedZone.isActive ? 'Désactivation...' : 'Activation...')
                  : (selectedZone.isActive ? 'Désactiver' : 'Activer')
                }
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${selectedZone.isActive ? 'bg-red-100' : 'bg-green-100'}`}>
              <svg className={`h-6 w-6 ${selectedZone.isActive ? 'text-red-600' : 'text-green-600'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                {selectedZone.isActive
                  ? `Êtes-vous sûr de vouloir désactiver la zone ${selectedZone.name} ?`
                  : `Êtes-vous sûr de vouloir activer la zone ${selectedZone.name} ?`
                }
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Zone Modal */}
      {showCreateModal && (
        <Modal
          open
          onClose={() => setShowCreateModal(false)}
          title="Créer une zone"
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
                className="modal-btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleCreateZone}
                disabled={createLoading}
                className="modal-btn-primary"
              >
                {createLoading ? 'Création...' : 'Confirmer'}
              </button>
            </>
          }
        >
          <div>
            <label htmlFor="zoneName" className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la zone
            </label>
            <select
              id="zoneName"
              value={newZoneName}
              onChange={(e) => setNewZoneName(e.target.value)}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {ZONE_NAMES.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ZoneManagement;
